import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "./ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  LayoutDashboard,
  PenSquare,
  Lightbulb,
  Handshake,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronDown,
  Calendar,
  BarChart3,
  Users,
  Image,
  Hash,
  FileText,
  Eye,
  TrendingUp,
  Bookmark,
  Contact,
  CreditCard
} from "lucide-react";
import { NeuralLogo } from "./NeuralLogo";

// Logo component using the actual StratIA logo
const StratIALogo = ({ size = 32 }) => (
  <img 
    src="/stratia-logo.png" 
    alt="StratIA" 
    className="rounded-lg"
    style={{ width: size, height: size }}
  />
);

// Los 4 pilares principales
const mainPillars = [
  { 
    id: "dashboard",
    icon: <LayoutDashboard className="w-5 h-5" />, 
    label: "Dashboard", 
    path: "/dashboard",
    description: "Métricas y calendario"
  },
  { 
    id: "editor",
    icon: <PenSquare className="w-5 h-5" />, 
    label: "Editor", 
    path: "/editor",
    description: "Crear y editar posts"
  },
  { 
    id: "ideas",
    icon: <Lightbulb className="w-5 h-5" />, 
    label: "Ideas", 
    path: "/ideas",
    description: "Inspiración y trends"
  },
  { 
    id: "brands",
    icon: <Handshake className="w-5 h-5" />, 
    label: "Brands", 
    path: "/brands",
    description: "Contactos y marcas"
  }
];

// Sub-navegación para cada pilar
const subNavigation = {
  dashboard: [
    { icon: <LayoutDashboard className="w-4 h-4" />, label: "Vista General", path: "/dashboard" },
    { icon: <Calendar className="w-4 h-4" />, label: "Calendario", path: "/calendar" },
    { icon: <BarChart3 className="w-4 h-4" />, label: "Analytics", path: "/analytics" },
    { icon: <Users className="w-4 h-4" />, label: "Cuentas", path: "/accounts" },
  ],
  editor: [
    { icon: <PenSquare className="w-4 h-4" />, label: "Crear Post", path: "/composer" },
    { icon: <Image className="w-4 h-4" />, label: "Galería", path: "/media" },
    { icon: <FileText className="w-4 h-4" />, label: "Plantillas", path: "/templates" },
    { icon: <Hash className="w-4 h-4" />, label: "Hashtags IA", path: "/composer?tool=hashtags" },
  ],
  ideas: [
    { icon: <Eye className="w-4 h-4" />, label: "Asesor IA", path: "/advisor" },
    { icon: <TrendingUp className="w-4 h-4" />, label: "Trends", path: "/ideas/trends" },
    { icon: <Bookmark className="w-4 h-4" />, label: "Colecciones", path: "/ideas/collections" },
  ],
  brands: [
    { icon: <Contact className="w-4 h-4" />, label: "Contactos", path: "/brands/contacts" },
    { icon: <Handshake className="w-4 h-4" />, label: "Colaboraciones", path: "/brands/collaborations" },
  ]
};

// Determinar el pilar activo basado en la ruta actual
const getActivePillar = (pathname) => {
  if (pathname.startsWith('/dashboard') || pathname.startsWith('/calendar') || 
      pathname.startsWith('/analytics') || pathname.startsWith('/accounts')) {
    return 'dashboard';
  }
  if (pathname.startsWith('/composer') || pathname.startsWith('/media') || 
      pathname.startsWith('/templates') || pathname.startsWith('/editor')) {
    return 'editor';
  }
  if (pathname.startsWith('/advisor') || pathname.startsWith('/ideas')) {
    return 'ideas';
  }
  if (pathname.startsWith('/brands')) {
    return 'brands';
  }
  return 'dashboard';
};

export const AppNavigation = ({ user, logout }) => {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const activePillar = getActivePillar(location.pathname);

  const DesktopSidebar = () => (
    <aside className="hidden lg:flex lg:fixed lg:inset-y-0 lg:left-0 lg:w-64 lg:flex-col bg-card border-r border-border">
      {/* Logo */}
      <div className="p-6 border-b border-border">
        <Link to="/dashboard" className="flex items-center gap-3" data-testid="sidebar-logo">
          <StratIALogo size={32} />
          <span className="font-bold text-xl tracking-tight gradient-neural-text">StratIA</span>
        </Link>
      </div>

      {/* Pilares principales */}
      <div className="p-4 border-b border-border">
        <div className="grid grid-cols-4 gap-1 bg-secondary/50 rounded-lg p-1">
          {mainPillars.map((pillar) => {
            const isActive = activePillar === pillar.id;
            return (
              <Link
                key={pillar.id}
                to={pillar.path}
                className={`flex flex-col items-center justify-center py-2 px-1 rounded-md transition-all ${
                  isActive
                    ? 'bg-gradient-to-b from-primary/20 to-accent/20 text-primary shadow-sm'
                    : 'text-muted-foreground hover:text-foreground hover:bg-secondary'
                }`}
                data-testid={`pillar-${pillar.id}`}
                title={pillar.description}
              >
                <span className={isActive ? 'text-primary' : ''}>{pillar.icon}</span>
                <span className="text-[10px] font-medium mt-1">{pillar.label}</span>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Sub-navegación del pilar activo */}
      <nav className="flex-1 p-4 overflow-y-auto">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 px-2">
          {mainPillars.find(p => p.id === activePillar)?.label}
        </p>
        <ul className="space-y-1">
          {subNavigation[activePillar]?.map((item) => {
            const isActive = location.pathname === item.path || 
                            (item.path.includes('?') && location.pathname + location.search === item.path);
            return (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all text-sm ${
                    isActive
                      ? 'bg-gradient-to-r from-primary/20 to-accent/20 text-primary border border-primary/30'
                      : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                  }`}
                  data-testid={`subnav-${item.label.toLowerCase().replace(' ', '-')}`}
                >
                  <span className={isActive ? 'text-primary' : ''}>{item.icon}</span>
                  <span className="font-medium">{item.label}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Accesos rápidos */}
      <div className="p-4 border-t border-border">
        <Link
          to="/subscription"
          className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:bg-secondary hover:text-foreground transition-all"
          data-testid="nav-subscription"
        >
          <CreditCard className="w-4 h-4" />
          <span className="font-medium">Suscripción</span>
        </Link>
        <Link
          to="/settings"
          className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:bg-secondary hover:text-foreground transition-all"
          data-testid="nav-settings"
        >
          <Settings className="w-4 h-4" />
          <span className="font-medium">Configuración</span>
        </Link>
      </div>

      {/* User Profile */}
      <div className="p-4 border-t border-border">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className="w-full justify-start gap-3 px-3"
              data-testid="user-menu-btn"
            >
              <Avatar className="w-8 h-8">
                <AvatarImage src={user?.picture} alt={user?.name} />
                <AvatarFallback className="bg-primary/10 text-primary">
                  {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 text-left">
                <p className="text-sm font-medium truncate">{user?.name || 'Usuario'}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
              </div>
              <ChevronDown className="w-4 h-4 text-muted-foreground" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuItem asChild>
              <Link to="/settings" className="flex items-center gap-2">
                <Settings className="w-4 h-4" />
                Configuración
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={logout}
              className="text-destructive focus:text-destructive"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Cerrar Sesión
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </aside>
  );

  const MobileNavigation = () => (
    <>
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-card/95 backdrop-blur-md border-b border-border">
        <div className="flex items-center justify-between px-4 h-14">
          <Link to="/dashboard" className="flex items-center gap-2">
            <StratIALogo size={28} />
            <span className="font-bold text-lg gradient-neural-text">StratIA</span>
          </Link>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="mobile-nav-btn"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
        </div>
      </header>

      {/* Mobile Bottom Navigation - Los 4 pilares */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-card/95 backdrop-blur-md border-t border-border safe-area-bottom">
        <div className="grid grid-cols-4 h-16">
          {mainPillars.map((pillar) => {
            const isActive = activePillar === pillar.id;
            return (
              <Link
                key={pillar.id}
                to={pillar.path}
                className={`flex flex-col items-center justify-center gap-1 transition-all ${
                  isActive
                    ? 'text-primary'
                    : 'text-muted-foreground'
                }`}
                data-testid={`mobile-pillar-${pillar.id}`}
              >
                <div className={`p-1.5 rounded-lg transition-all ${
                  isActive ? 'bg-primary/10' : ''
                }`}>
                  {pillar.icon}
                </div>
                <span className="text-[10px] font-medium">{pillar.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>

      {/* Mobile Sidebar Overlay for sub-navigation */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40 pt-14">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setMobileOpen(false)}
          />
          <div className="absolute top-14 left-0 bottom-16 w-72 bg-card border-r border-border overflow-y-auto">
            {/* Sub-navegación del pilar activo */}
            <div className="p-4">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                {mainPillars.find(p => p.id === activePillar)?.label}
              </p>
              <ul className="space-y-1">
                {subNavigation[activePillar]?.map((item) => {
                  const isActive = location.pathname === item.path;
                  return (
                    <li key={item.path}>
                      <Link
                        to={item.path}
                        className={`flex items-center gap-3 px-3 py-3 rounded-lg transition-all ${
                          isActive
                            ? 'bg-gradient-to-r from-primary/20 to-accent/20 text-primary'
                            : 'text-muted-foreground hover:bg-secondary'
                        }`}
                        onClick={() => setMobileOpen(false)}
                      >
                        {item.icon}
                        <span className="font-medium">{item.label}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* Quick links */}
            <div className="p-4 border-t border-border">
              <Link
                to="/subscription"
                className="flex items-center gap-3 px-3 py-3 rounded-lg text-muted-foreground hover:bg-secondary"
                onClick={() => setMobileOpen(false)}
              >
                <CreditCard className="w-4 h-4" />
                <span>Suscripción</span>
              </Link>
              <Link
                to="/settings"
                className="flex items-center gap-3 px-3 py-3 rounded-lg text-muted-foreground hover:bg-secondary"
                onClick={() => setMobileOpen(false)}
              >
                <Settings className="w-4 h-4" />
                <span>Configuración</span>
              </Link>
            </div>

            {/* User */}
            <div className="p-4 border-t border-border">
              <div className="flex items-center gap-3 px-3 py-2">
                <Avatar className="w-10 h-10">
                  <AvatarImage src={user?.picture} alt={user?.name} />
                  <AvatarFallback className="bg-primary/10 text-primary">
                    {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <p className="font-medium">{user?.name || 'Usuario'}</p>
                  <p className="text-xs text-muted-foreground">{user?.email}</p>
                </div>
              </div>
              <Button
                variant="ghost"
                className="w-full justify-start gap-3 mt-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                onClick={logout}
              >
                <LogOut className="w-4 h-4" />
                Cerrar Sesión
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );

  return (
    <>
      <DesktopSidebar />
      <MobileNavigation />
    </>
  );
};

export default AppNavigation;
